import type { dcs } from "@dropbox/api-v2-client";
import { ServiceId } from "@mirage/discovery/id";
import * as services from "@mirage/discovery/services";
import { toPeopleRecommendation } from "@mirage/shared/search/people-recommendations";

import type { dash, search_suggestion, sharing } from "@dropbox/api-v2-client";
import type { APIv2Callable } from "@mirage/service-dbx-api/service";
import type { LogoutServiceConsumerContract } from "@mirage/service-logout";
import type {
  PersonObject,
  PersonOrgChart,
  PersonTypeaheadSuggestion,
} from "@mirage/service-people/types";
import type { ConsolaInstance } from "consola";

export interface Service {
  getOrgChart(): Promise<PersonOrgChart[]>;
  getPeopleSuggestions(
    options: GetPeopleSuggestionsOptions,
  ): Promise<PersonObject[]>;
  getPeopleTypeaheadSuggestions(): Promise<PersonTypeaheadSuggestion[]>;
  performPeopleSearch(query: string): Promise<PersonObject[]>;
  tearDown(): Promise<void>;
}

interface DbxApiServiceContract {
  callApiV2: APIv2Callable;
}

interface LoggingServiceContract {
  tagged: (tag: string) => ConsolaInstance;
}

export type GetPeopleSuggestionsOptions = {
  limit?: number;
  returnTeamMembersIfEmpty?: boolean;
};

export default function peopleService(
  { callApiV2 }: DbxApiServiceContract,
  logoutService: LogoutServiceConsumerContract,
  { tagged }: LoggingServiceContract,
) {
  const logger = tagged("people-service");

  let fetchPromiseOrgChart: Promise<PersonOrgChart[]> | null = null;
  let fetchPromisePeopleSuggestions: Promise<PersonObject[]> | null = null;
  let fetchPromisePeopleTypeaheadSuggestions: Promise<
    PersonTypeaheadSuggestion[]
  > | null = null;

  const getOrgChart = async () => {
    if (!fetchPromiseOrgChart) {
      fetchPromiseOrgChart = (async () => {
        const res = await callApiV2("dashPeopleListPeople", {});
        if (res.people === undefined) {
          throw new Error(
            'Unexpected response from "dashPeopleListPeople"; people is undefined',
          );
        }

        const peopleByUuid: Map<string, PersonOrgChart> = new Map();
        res.people.forEach((user: dash.BasicPerson) => {
          if (!user.uuid) {
            return;
          }
          peopleByUuid.set(user.uuid, {
            uuid: user.uuid,
            accountId: user.account_id ?? "",
            managerUuid: user.manager_uuid,
            // Some contractors don't have an email assigned so we need to hide it from UI
            email:
              user.email === "donotuse@dropbox.com" ? "" : (user.email ?? ""),
            displayName: user.name ?? "",
            displayHandle: user.display_handle ?? "",
            profilePhotoUrl: user.profile_photo_url,
            directReportCount: 0,
            totalReportCount: 0,
            onLeave: user.on_leave ?? false,
            isDeactivated: user.is_deactivated ?? false,
            role: user.role ?? "",
            teams: user.teams ?? [],
            birthday: user.birthday || null,
            tenure: user.tenure_undefined ? null : user.tenure || 0,
          });

          let person: PersonOrgChart | undefined = peopleByUuid.get(user.uuid);
          if (person?.managerUuid) {
            // Increment the direct report count for this person's manager
            const manager = peopleByUuid.get(person.managerUuid);
            if (manager && manager.directReportCount !== undefined) {
              manager.directReportCount++;
            }
          }

          // Increment the total report count for this person's manager, their manager's manager, etc.
          while (person?.managerUuid) {
            const manager = peopleByUuid.get(person.managerUuid);
            if (manager && manager.totalReportCount !== undefined) {
              manager.totalReportCount++;
            }
            person = peopleByUuid.get(person.managerUuid);
          }
        });

        logger.debug("[orgChart] returning HRIS results from API");
        return Array.from(peopleByUuid, ([_, v]) => v);
      })();
    }
    try {
      return await fetchPromiseOrgChart;
    } finally {
      fetchPromiseOrgChart = null;
    }
  };

  const getPeopleSuggestions = async (
    options: GetPeopleSuggestionsOptions = {},
  ) => {
    if (!fetchPromisePeopleSuggestions) {
      fetchPromisePeopleSuggestions = (async () => {
        try {
          const res = await callApiV2("searchSuggestionGetPeopleSuggestion", {
            num_items: options.limit ?? 5,
            return_team_members_if_empty:
              options.returnTeamMembersIfEmpty ?? false,
          });

          const response = (res.people_suggestions ?? []).map(
            (user: search_suggestion.SuggestedUser) =>
              toPeopleRecommendation({
                accountId: user.account_id ?? "",
                displayName: user.display_name ?? "",
                email: user.email ?? "",
                profilePhotoUrl: user.photo_url,
                score: user.score ?? 0,
              }),
          );

          logger.debug("[suggestions] returning results from API");
          return response;
        } catch (err) {
          // silently handle errors. Suggestions are a bonus, not required.
          logger.debug(
            "[suggestions] caught error calling searchSuggestionGetPeopleSuggestion",
            err,
          );
        }
        return [];
      })();
    }

    try {
      return await fetchPromisePeopleSuggestions;
    } finally {
      fetchPromisePeopleSuggestions = null;
    }
  };

  const getPeopleTypeaheadSuggestions = async () => {
    if (!fetchPromisePeopleTypeaheadSuggestions) {
      fetchPromisePeopleTypeaheadSuggestions = (async () => {
        const res = await callApiV2("dcsGetTypeaheadSuggestions", {
          suggestion_type: { ".tag": "suggestion_type_people" },
          max_suggestions: 150,
        });
        if (!res?.people_suggestions?.suggestions?.length) {
          logger.debug(
            "[suggestions] no suggestions from get_typeahead_suggestions",
          );
          return [];
        }

        const response = (res.people_suggestions?.suggestions || []).map(
          (person: dcs.SuggestedPeople) => ({
            // person_uuid is present, but empty as of 2025-06-27
            // Once available, it should be used here and added to PersonTypeaheadSuggestion type
            //uuid: person.person_uuid ?? "",
            accountId: person.account_id ?? "",
            score: person.score ?? 0,
            predictionId: person.prediction_id ?? "",
            reason: person.reason ?? "",
          }),
        );

        logger.debug(
          "[suggestions] returning results from get_typeahead_suggestions API",
        );
        return response;
      })();
    }

    try {
      return await fetchPromisePeopleTypeaheadSuggestions;
    } finally {
      fetchPromisePeopleTypeaheadSuggestions = null;
    }
  };

  type SortablePersonObject = PersonObject & { sortKey: string };
  const fetchPeopleFromServer = async (
    query: string,
  ): Promise<PersonObject[]> => {
    const res = await callApiV2("sharingTargetsSearch", {
      limit: 5,
      query,
    });

    return res.entries
      .reduce(
        (
          filtered: SortablePersonObject[],
          entry:
            | sharing.GroupTargetReference
            | sharing.TargetReference
            | sharing.UserTargetReference,
        ) => {
          if (entry[".tag"] === "user" && "name" in entry) {
            filtered.push({
              displayName: entry.name,
              email: entry.email,
              profilePhotoUrl: entry.photo_url,
              sortKey: entry.sort_key,
            });
          }
          return filtered;
        },
        [],
      )
      .sort((entry1: SortablePersonObject, entry2: SortablePersonObject) => {
        if (!entry1.sortKey && !entry2.sortKey) {
          return 0;
        } else if (!entry1.sortKey) {
          return 1;
        } else if (!entry2.sortKey) {
          return -1;
        }
        return entry1.sortKey < entry2.sortKey ? 1 : -1;
      });
  };

  const performPeopleSearch = async (query: string) => {
    logger.debug("[search] searching for people");

    const peopleFromServer = await fetchPeopleFromServer(query);

    return peopleFromServer;
  };

  const tearDown = async () => {
    fetchPromiseOrgChart = null;
    fetchPromisePeopleSuggestions = null;
    fetchPromisePeopleTypeaheadSuggestions = null;
  };

  const service = {
    getOrgChart,
    getPeopleSuggestions,
    getPeopleTypeaheadSuggestions,
    performPeopleSearch,
    tearDown,
  };

  logoutService.registerLogoutCallback(ServiceId.PEOPLE, async () => {
    await tearDown();
  });

  return services.provide<Service>(ServiceId.PEOPLE, service, [
    ServiceId.DBX_API,
    ServiceId.TYPEAHEAD_SEARCH,
  ]);
}
